// Relatório de gestão do suporte por período: filtros, comparação com o período anterior e 9 abas.
// Qualquer número clicável abre a lista de chamados por trás dele; Excel com uma aba por relatório.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Tab, Tabs, TextField, Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import PrintOutlinedIcon from "@material-ui/icons/PrintOutlined";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import suporteApi, { baixar, mensagemErro } from "../api";
import { DialogCard, dataBR } from "../comum";
import { Barras, Kpi, Pill, hm, pillPrazo, useEstilosGestao } from "./Hoje";

// data local (toISOString usa UTC e à noite viraria o dia seguinte)
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ATALHOS = [
  ["Hoje", () => [new Date(), new Date()]],
  ["7 dias", () => [new Date(Date.now() - 6 * 864e5), new Date()]],
  ["30 dias", () => [new Date(Date.now() - 29 * 864e5), new Date()]],
  ["Mês atual", () => { const h = new Date(); return [new Date(h.getFullYear(), h.getMonth(), 1), h]; }],
  ["Mês anterior", () => { const h = new Date(); return [new Date(h.getFullYear(), h.getMonth() - 1, 1), new Date(h.getFullYear(), h.getMonth(), 0)]; }],
];
const ABAS = [["geral", "Visão geral"], ["tempos", "Tempos (SLA)"], ["clientes", "Clientes"], ["sistemas", "Sistemas e serviços"],
  ["tecnicos", "Técnicos"], ["retornos", "Retornos"], ["equipe", "Equipe"], ["formularios", "Formulários"], ["conversas", "Conversas (WhatsApp)"]];
const DIAS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

// número do card clicável dentro de uma célula (abre o card por cima do relatório)
const LinkCard = ({ id, abrir }) => (
  <Button size="small" color="primary" style={{ minWidth: 0, padding: "0 4px", fontWeight: 700 }}
    onClick={(e) => { e.stopPropagation(); abrir(id); }}>{`#${id}`}</Button>
);

// variação vs período anterior: "bom" depende do indicador (mais chamados não é bom nem ruim)
const Delta = ({ atual, antes, menorMelhor, unidade = "%" }) => {
  if (atual === null || atual === undefined || antes === null || antes === undefined || antes === 0) return <span>sem base anterior</span>;
  const dif = unidade === "p.p." ? atual - antes : Math.round((100 * (atual - antes)) / antes);
  if (dif === 0) return <span>igual ao período anterior</span>;
  const bom = menorMelhor === undefined ? null : menorMelhor ? dif < 0 : dif > 0;
  const cor = bom === null ? "inherit" : bom ? "#2e7d32" : "#d32f2f";
  return <span style={{ color: cor, fontWeight: 600 }}>{`${dif > 0 ? "▲" : "▼"} ${Math.abs(dif)}${unidade === "p.p." ? " p.p." : "%"} vs anterior`}</span>;
};

const Tabela = ({ colunas, linhas, onLinha }) => {
  const c = useEstilosGestao();
  const [ordem, setOrdem] = useState(null);
  const ord = ordem ? [...linhas].sort((a, b) => {
    const x = a[ordem.k], y = b[ordem.k];
    return (x === y ? 0 : x === null || x === undefined ? 1 : y === null || y === undefined ? -1 : x > y ? 1 : -1) * (ordem.desc ? -1 : 1);
  }) : linhas;
  if (!linhas.length) return <div className={c.vazio}>Nada no período.</div>;
  return (
    <div className={c.tabela}><table>
      <thead><tr>{colunas.map((col) => (
        <th key={col.k} className={col.n ? "n" : ""} style={{ cursor: "pointer" }} title="Ordenar"
          onClick={() => setOrdem((o) => ({ k: col.k, desc: o?.k === col.k ? !o.desc : true }))}>
          {col.t}{ordem?.k === col.k ? (ordem.desc ? " ▾" : " ▴") : ""}</th>))}</tr></thead>
      <tbody>{ord.map((l, i) => (
        <tr key={i} style={onLinha ? { cursor: "pointer" } : undefined} onClick={onLinha ? () => onLinha(l) : undefined}>
          {colunas.map((col) => <td key={col.k} className={col.n ? "n" : ""}>{col.f ? col.f(l[col.k], l) : l[col.k] ?? "—"}</td>)}
        </tr>))}</tbody>
    </table></div>
  );
};

const Calor = ({ dados }) => {
  const horas = Array.from({ length: 16 }, (_, i) => 6 + i);
  const v = {};
  dados.forEach((d) => { v[`${d.dia}-${d.hora}`] = d.n; });
  const max = Math.max(1, ...dados.map((d) => d.n));
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: `36px repeat(${horas.length}, minmax(18px, 1fr))`, gap: 3, minWidth: 420, fontSize: 11 }}>
        <span />
        {horas.map((h) => <span key={h} style={{ textAlign: "center", opacity: 0.7 }}>{h % 2 ? "" : `${h}h`}</span>)}
        {DIAS.map((dn, i) => (
          <React.Fragment key={dn}>
            <span style={{ opacity: 0.7 }}>{dn}</span>
            {horas.map((h) => {
              const n = v[`${i + 1}-${h}`] || 0;
              return <div key={h} title={`${dn} ${h}h: ${n} chamado(s)`}
                style={{ aspectRatio: "1", borderRadius: 3, background: n ? `rgba(59,122,20,${0.15 + (0.85 * n) / max})` : "rgba(127,127,127,.12)" }} />;
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const RelatorioGestao = () => {
  const c = useEstilosGestao();
  const [f, setF] = useState(() => ({ de: iso(new Date(Date.now() - 29 * 864e5)), ate: iso(new Date()), cliente: "", sistema: "", tipo: "", qra: "", atendente: "" }));
  const [d, setD] = useState(null);
  const [aba, setAba] = useState("geral");
  const [lista, setLista] = useState(null); // drill-down: {titulo, itens}
  const [card, setCard] = useState(null);

  const params = useCallback((extra = {}) => {
    const p = { ...f, ...extra };
    Object.keys(p).forEach((k) => (p[k] === "" || p[k] === null) && delete p[k]);
    return p;
  }, [f]);
  const carregar = useCallback(async () => {
    try {
      setD((await suporteApi.get("/api/gestao/relatorio", { params: params() })).data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, [params]);
  useEffect(() => { carregar(); }, [carregar]);

  const abrirLista = async (titulo, extra) => {
    try {
      const { data } = await suporteApi.get("/api/gestao/relatorio/chamados", { params: params(extra) });
      setLista({ titulo, itens: data });
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const campo = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), size: "small", variant: "outlined" });

  if (!d) return <div className={c.raiz}><Typography color="textSecondary">Carregando…</Typography></div>;
  const s = d.resumo, a = d.anterior;

  const conteudo = () => {
    switch (aba) {
      case "geral": return (
        <>
          <div className={c.kpis}>
            <Kpi rot="Chamados no período" val={s.total} det={<Delta atual={s.total} antes={a.total} />} onClick={() => abrirLista("Chamados do período", {})} />
            <Kpi rot="Finalizados" val={s.finalizados} tom="ok" det={s.total ? `${Math.round((100 * s.finalizados) / s.total)}% dos abertos` : ""} />
            <Kpi rot="Tempo médio de atendimento" val={hm(s.tempo)} det={<Delta atual={s.tempo} antes={a.tempo} menorMelhor />} />
            <Kpi rot="Dentro do prazo" val={s.no_prazo === null ? "—" : `${s.no_prazo}%`} tom={s.no_prazo >= 90 ? "ok" : s.no_prazo >= 75 ? "warn" : "crit"}
              det={<Delta atual={s.no_prazo} antes={a.no_prazo} menorMelhor={false} unidade="p.p." />} />
            <Kpi rot="1ª resposta média" val={hm(s.resp)} det={<Delta atual={s.resp} antes={a.resp} menorMelhor />} />
            <Kpi rot="Internos" val={s.internos} det={s.total ? `${Math.round((100 * s.internos) / s.total)}% do total` : ""} onClick={() => abrirLista("Chamados internos", { tipo: "INTERNO" })} />
          </div>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Abertos × finalizados por dia</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={d.diario} margin={{ left: -18, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="dia" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="abertos" name="Abertos" stroke="#3B7A14" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="finalizados" name="Finalizados" stroke="#85C440" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Quando os chamados chegam <span className={c.sub}>dia da semana × hora de abertura</span></div>
            <Calor dados={d.calor} />
          </Paper>
        </>
      );
      case "tempos": return (
        <>
          <div className={c.kpis}>
            <Kpi rot="Tempo médio" val={hm(s.tempo)} det="do início ao fim do card" />
            <Kpi rot="Mediana" val={hm(s.mediana)} det="metade fecha em até esse tempo" />
            <Kpi rot="10% mais lentos" val={hm(s.p90)} tom="warn" det="levaram mais que isso" />
            <Kpi rot="1ª resposta" val={hm(s.resp)} det="abertura do card → 1ª mensagem do suporte" />
            <Kpi rot="Tempo parado (pendente)" val={`${s.parado_pct ?? 0}%`} det="do tempo total dos cards" />
          </div>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Prazo por tipo de serviço</div>
            <Tabela colunas={[{ k: "tipo", t: "Tipo" }, { k: "meta", t: "Meta", f: (v) => (v ? hm(v) : "—") },
              { k: "total", t: "Chamados", n: 1 }, { k: "tempo", t: "Tempo médio", n: 1, f: hm }, { k: "no_prazo", t: "No prazo", n: 1, f: pillPrazo }]}
              linhas={d.por_tipo} onLinha={(l) => abrirLista(`Tipo ${l.tipo}`, { tipo: l.tipo })} />
          </Paper>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Quanto tempo o card fica aberto <span className={c.sub}>chamados finalizados</span></div>
            <Barras linhas={d.faixas.map((x) => ({ nome: x.faixa, total: x.total }))} />
          </Paper>
        </>
      );
      case "clientes": return (
        <>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Por cliente <span className={c.sub}>clique para ver os chamados</span></div>
            <Tabela colunas={[{ k: "cliente", t: "Cliente" }, { k: "total", t: "Chamados", n: 1 }, { k: "tempo", t: "Tempo médio aberto", n: 1, f: hm },
              { k: "no_prazo", t: "No prazo", n: 1, f: pillPrazo }, { k: "lojas", t: "Lojas atendidas", n: 1 },
              { k: "voltaram", t: "Voltaram em 30 dias", n: 1, f: (v) => (v === null ? "—" : <Pill tom={v <= 10 ? "ok" : v <= 20 ? "warn" : "crit"}>{`${v}%`}</Pill>) }]}
              linhas={d.clientes} onLinha={(l) => abrirLista(`Cliente ${l.cliente}`, { cliente: l.cliente })} />
            <div className={c.nota}>"Voltaram em 30 dias" (contado por O.S): das O.S finalizadas, quantas tiveram retorno — a mesma loja abriu
              outro chamado técnico do mesmo sistema, com outra O.S, depois de finalizado e em até 30 dias. Não contam chamado interno, card aberto por formulário,
              outro sistema (ex.: CFTV e alarme), outro card da mesma O.S e continuação.</div>
          </Paper>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Lojas que mais chamaram</div>
            <Tabela colunas={[{ k: "conta", t: "Conta" }, { k: "loja", t: "Loja" }, { k: "cliente", t: "Cliente" }, { k: "total", t: "Chamados", n: 1 },
              { k: "ultimo_sistema", t: "Último sistema" }, { k: "central", t: "Central" }]}
              linhas={d.contas} onLinha={(l) => abrirLista(`Conta ${l.conta}`, { conta: l.conta })} />
          </Paper>
        </>
      );
      case "sistemas": return (
        <>
          <div className={c.grade}>
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>Chamados por sistema</div>
              <Barras linhas={d.sistemas.map((x) => ({ nome: x.sistema, total: x.total }))} onClick={(l) => abrirLista(`Sistema ${l.nome}`, { sistema: l.nome })} />
            </Paper>
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>Tempo médio por sistema</div>
              <Barras linhas={d.sistemas.map((x) => ({ nome: x.sistema, total: x.tempo || 0 }))} fmt={hm} />
            </Paper>
          </div>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Causas mais comuns <span className={c.sub}>campo "Causa / solução" ao finalizar</span></div>
            <Barras linhas={d.causas.map((x) => ({ nome: x.causa, total: x.total }))} onClick={(l) => abrirLista(`Causa ${l.nome}`, { causa: l.nome })} />
            {!d.causas.length && <div className={c.nota}>Começa a encher conforme os chamados forem finalizados com a causa.</div>}
          </Paper>
        </>
      );
      case "tecnicos": return (
        <Paper variant="outlined" className={c.painel}>
          <div className={c.titulo}>Técnicos <span className={c.sub}>clique para ver os atendimentos</span></div>
          <Tabela colunas={[{ k: "qra", t: "QRA", n: 1 }, { k: "tecnico", t: "Técnico" }, { k: "total", t: "Atendimentos", n: 1 },
            { k: "tempo", t: "Tempo médio", n: 1, f: hm }, { k: "form_no_prazo", t: "Formulários no prazo", n: 1, f: pillPrazo },
            { k: "correcoes", t: "Correções pedidas", n: 1 },
            { k: "voltaram", t: "Voltou à mesma loja", n: 1, f: (v) => (v === null ? "—" : <Pill tom={v <= 10 ? "ok" : v <= 20 ? "warn" : "crit"}>{`${v}%`}</Pill>) }]}
            linhas={d.tecnicos} onLinha={(l) => abrirLista(`QRA ${l.qra} · ${l.tecnico || ""}`, { qra: l.qra })} />
          <div className={c.nota}>"Voltou à mesma loja": outro chamado na mesma loja e no mesmo sistema em até 30 dias depois do atendimento dele.</div>
        </Paper>
      );
      case "retornos": {
        const rt = d.retornos;
        const tomPct = (v) => (v === null || v === undefined ? "—" : <Pill tom={v <= 5 ? "ok" : v <= 10 ? "warn" : "crit"}>{`${v}%`}</Pill>);
        return (
          <>
            <div className={c.kpis}>
              <Kpi rot="O.S finalizadas" val={rt.base} det="chamados técnicos com loja e O.S" />
              <Kpi rot="Com retorno" val={rt.com_retorno} tom={rt.com_retorno ? "warn" : "ok"} det="mesma loja e sistema, outra O.S, até 30 dias" />
              <Kpi rot="Taxa de retorno" val={rt.pct === null ? "—" : `${rt.pct}%`} tom={rt.pct <= 5 ? "ok" : rt.pct <= 10 ? "warn" : "crit"} det="das O.S finalizadas" />
              <Kpi rot="2ª visita" val={rt.segunda_visita} det="O.S do retorno derivada da 1ª (ex.: 1061412)" />
            </div>
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>Por cliente</div>
              <Tabela colunas={[{ k: "cliente", t: "Cliente" }, { k: "os", t: "O.S finalizadas", n: 1 }, { k: "retornos", t: "Com retorno", n: 1 },
                { k: "pct", t: "Taxa", n: 1, f: tomPct }]} linhas={rt.por_cliente} />
            </Paper>
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>{`O.S com retorno (${rt.pares.length})`} <span className={c.sub}>clique no número do card (#) para abrir a 1ª O.S ou o retorno</span></div>
              {rt.pares.length ? (
                <Tabela colunas={[
                  { k: "conta", t: "Loja", f: (v, l) => <><b>{v}</b><div className={c.nota} style={{ margin: 0 }}>{l.filial}</div></> },
                  { k: "cliente", t: "Cliente" }, { k: "sistema", t: "Sistema" },
                  { k: "os", t: "1ª O.S", f: (v, l) => <>{v}<div className={c.nota} style={{ margin: 0 }}><LinkCard id={l.id} abrir={setCard} />{` · QRA ${l.qra} ${(l.tecnico || "").split(" ")[0]} · ${dataBR(l.fim)}`}</div><div className={c.nota} style={{ margin: 0 }}>{l.causa || ""}</div></> },
                  { k: "b_os", t: "Retorno", f: (v, l) => <>{v}{l.segunda_visita && <Chip size="small" label="2ª visita" style={{ marginLeft: 6 }} />}<div className={c.nota} style={{ margin: 0 }}><LinkCard id={l.b_id} abrir={setCard} />{` · QRA ${l.b_qra} ${(l.b_tecnico || "").split(" ")[0]}${l.mesmo_tecnico ? " (mesmo técnico)" : ""} · ${dataBR(l.b_inicio)}`}</div><div className={c.nota} style={{ margin: 0 }}>{l.b_causa || ""}</div></> },
                  { k: "intervalo", t: "Depois de", n: 1, f: (v) => hm(v) },
                ]} linhas={rt.pares} />
              ) : <Typography variant="body2" color="textSecondary">Nenhum retorno no período.</Typography>}
              <div className={c.nota}>
                Retorno = a mesma loja abriu outro chamado técnico do mesmo sistema, com outra O.S, depois de finalizado e em até 30 dias.
                Contado por O.S (vários cards da mesma O.S valem uma). Não entram: chamado interno, card sem loja ou sem O.S, card aberto
                por formulário, outro sistema e continuação.
              </div>
            </Paper>
          </>
        );
      }
      case "equipe": return (
        <>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Atendentes</div>
            <Tabela colunas={[{ k: "atendente", t: "Atendente" }, { k: "abriu", t: "Abriu", n: 1 }, { k: "finalizou", t: "Finalizou", n: 1 },
              { k: "tempo", t: "Tempo médio", n: 1, f: hm }, { k: "resp", t: "1ª resposta", n: 1, f: hm }, { k: "no_prazo", t: "No prazo", n: 1, f: pillPrazo }]}
              linhas={d.equipe} onLinha={(l) => abrirLista(`Atendente ${l.atendente}`, { atendente: l.atendente })} />
          </Paper>
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>{`Manuais enviados por operador (${d.manuais.total})`}</div>
            {d.manuais.por_operador.length ? (
              <Tabela colunas={[{ k: "operador", t: "Operador" }, { k: "total", t: "Manuais enviados", n: 1 },
                { k: "manuais", t: "Manuais diferentes", n: 1 }, { k: "conversas", t: "Conversas", n: 1 }]} linhas={d.manuais.por_operador} />
            ) : <Typography variant="body2" color="textSecondary">Nenhum manual enviado no período.</Typography>}
          </Paper>
          {!!d.manuais.por_manual.length && (
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>Manuais mais enviados</div>
              <Barras linhas={d.manuais.por_manual.map((x) => ({ nome: x.manual, total: x.total }))} />
            </Paper>
          )}
          <Paper variant="outlined" className={c.painel}>
            <div className={c.titulo}>Chamados abertos por hora do dia</div>
            <Barras linhas={d.por_hora.map((x) => ({ nome: `${String(x.hora).padStart(2, "0")}h`, total: x.n }))} />
          </Paper>
        </>
      );
      case "formularios": {
        const fo = d.formularios;
        return (
          <>
            <div className={c.kpis}>
              <Kpi rot="Enviados" val={fo.enviados} det="pelo card (link único)" />
              <Kpi rot="Respondidos" val={fo.respondidos} tom="ok" det={fo.enviados ? `${Math.round((100 * fo.respondidos) / fo.enviados)}%` : ""} />
              <Kpi rot="Expirados" val={fo.expirados} tom={fo.expirados ? "warn" : "ok"} det={`reenviados: ${fo.reenviados}`} />
              <Kpi rot="Técnico respondeu em" val={hm(fo.resposta)} det="média" />
              <Kpi rot="Validação em" val={hm(fo.validacao)} det="média do suporte" />
            </div>
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>Por formulário</div>
              <Tabela colunas={[{ k: "formulario", t: "Formulário" }, { k: "enviados", t: "Enviados", n: 1 }, { k: "respondidos", t: "Respondidos", n: 1 },
                { k: "correcoes", t: "Correções", n: 1 }, { k: "resposta", t: "Tempo de resposta", n: 1, f: hm }]} linhas={fo.por_formulario} />
            </Paper>
          </>
        );
      }
      case "conversas": {
        const cv = d.conversas || {};
        return (
          <>
            <div className={c.kpis}>
              <Kpi rot="Conversas" val={cv.total} det={(cv.por_fila || []).map((x) => `${x.nome} ${x.total}`).join(" · ")} />
              <Kpi rot="Espera até a 1ª resposta" val={hm(cv.espera_media)} tom={cv.espera_media > 15 ? "warn" : "ok"} det="média" />
              <Kpi rot="Conversas sem chamado" val={cv.sem_chamado} det="dúvidas rápidas, sem card" />
              <Kpi rot="Mensagens por conversa" val={cv.mensagens_media} det="média" />
            </div>
            <Paper variant="outlined" className={c.painel}>
              <div className={c.titulo}>Espera por hora do dia</div>
              <Barras linhas={(cv.espera_por_hora || []).map((x) => ({ nome: x.hora, total: x.minutos }))} fmt={hm} />
            </Paper>
          </>
        );
      }
      default: return null;
    }
  };

  return (
    <div className={c.raiz}>
      <Paper variant="outlined" className={`${c.painel} no-print`} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {ATALHOS.map(([n, fn]) => {
          const [de, ate] = fn();
          const ativo = f.de === iso(de) && f.ate === iso(ate);
          return <Chip key={n} label={n} clickable color={ativo ? "primary" : "default"} onClick={() => setF({ ...f, de: iso(de), ate: iso(ate) })} />;
        })}
        <TextField id="rel-de" label="De" type="date" InputLabelProps={{ shrink: true }} {...campo("de")} />
        <TextField id="rel-ate" label="Até" type="date" InputLabelProps={{ shrink: true }} {...campo("ate")} />
        <TextField id="rel-cli" select label="Cliente" style={{ minWidth: 140 }} {...campo("cliente")}>
          <MenuItem value="">Todos</MenuItem>{d.filtros.clientes.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField id="rel-sis" select label="Sistema" style={{ minWidth: 130 }} {...campo("sistema")}>
          <MenuItem value="">Todos</MenuItem>{d.filtros.sistemas.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField id="rel-tipo" select label="Tipo" style={{ minWidth: 130 }} {...campo("tipo")}>
          <MenuItem value="">Todos</MenuItem>{d.filtros.tipos.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <TextField id="rel-tec" select label="Técnico" style={{ minWidth: 150 }} {...campo("qra")}>
          <MenuItem value="">Todos</MenuItem>{d.filtros.tecnicos.map((x) => <MenuItem key={x.qra} value={x.qra}>{`${x.qra} · ${x.tecnico || ""}`}</MenuItem>)}
        </TextField>
        <TextField id="rel-at" select label="Atendente" style={{ minWidth: 140 }} {...campo("atendente")}>
          <MenuItem value="">Todos</MenuItem>{d.filtros.atendentes.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </TextField>
        <span style={{ flex: 1 }} />
        <Button variant="outlined" color="primary" startIcon={<GetAppIcon />}
          onClick={() => baixar("/api/gestao/relatorio.xlsx", params()).catch((e) => toast.error(mensagemErro(e)))}>Excel</Button>
        <Button variant="contained" color="primary" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>PDF / imprimir</Button>
      </Paper>
      <Typography variant="body2" color="textSecondary">
        {`${dataBR(d.periodo.de, true)} a ${dataBR(d.periodo.ate, true)} (${d.periodo.dias} dias) · comparado com ${dataBR(d.periodo.anterior.de, true)} a ${dataBR(d.periodo.anterior.ate, true)}`}
      </Typography>
      <Tabs value={aba} onChange={(e, v) => setAba(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" className="no-print">
        {ABAS.map(([id, n]) => <Tab key={id} value={id} label={n} />)}
      </Tabs>
      {conteudo()}
      <div className={c.nota}>Tabelas ordenam ao clicar no cabeçalho; linhas e números clicáveis abrem os chamados. O Excel traz uma aba por relatório e a lista de chamados.</div>

      <Dialog open={!!lista} onClose={() => setLista(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{lista ? `${lista.titulo} (${lista.itens.length})` : ""}</DialogTitle>
        <DialogContent>
          {lista && <Tabela colunas={[{ k: "inicio", t: "Início", f: (v) => dataBR(v) }, { k: "os", t: "O.S" }, { k: "filial", t: "Loja" },
            { k: "cliente", t: "Cliente" }, { k: "tipo", t: "Tipo" }, { k: "sistema", t: "Sistema" }, { k: "tecnico", t: "Técnico" },
            { k: "analista", t: "Atendente" }, { k: "minutos", t: "Tempo", n: 1, f: (v, l) => (v === null ? (l.status === "ABERTO" ? "aberto" : "—") : <Pill tom={l.meta && v > l.meta ? "crit" : "ok"}>{hm(Number(v))}</Pill>) },
            { k: "causa", t: "Causa" }]} linhas={lista.itens} onLinha={(l) => setCard(l.id)} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setLista(null)}>Fechar</Button></DialogActions>
      </Dialog>
      <DialogCard id={card} open={!!card} onClose={() => setCard(null)} />
    </div>
  );
};

export default RelatorioGestao;
